import React, { useState } from "react";
import { RefreshCw, Trash2 } from "lucide-react";
import { PortfolioVideoUploadField } from "@/pages/admin/fields/PortfolioVideoUploadField";

const readVideoMeta = (url) => new Promise((resolve) => {
  const v = document.createElement("video");
  let done = false;
  const finish = (m) => { if (done) return; done = true; v.removeAttribute("src"); v.load(); resolve(m); };
  v.preload = "metadata";
  v.muted = true;
  v.playsInline = true;
  v.onloadedmetadata = () => finish({
    width: v.videoWidth || 0,
    height: v.videoHeight || 0,
    duration: Number.isFinite(v.duration) ? Math.round(v.duration * 10) / 10 : 0,
  });
  v.onerror = () => finish({ width: 0, height: 0, duration: 0 });
  setTimeout(() => finish({ width: 0, height: 0, duration: 0 }), 10000);
  v.src = url;
});

const shapeLabel = (w, h) => {
  if (!w || !h) return "";
  const r = w / h;
  if (r >= 1.3) return "Landscape";
  if (r < 0.85) return "Portrait";
  return "Square";
};

// One optional project video — used by the homepage Featured card AND the project page player.
export const PortfolioVideoCoverField = ({ value, meta, onChange }) => {
  const [replacing, setReplacing] = useState(false);

  const handleUploaded = async (url) => {
    if (!url) return;
    const m = await readVideoMeta(url);
    const base = process.env.REACT_APP_BACKEND_URL;
    const portable = base && url.startsWith(base) ? url.slice(base.length) : url;
    onChange(portable, { ...m, uploaded_at: new Date().toISOString() });
    setReplacing(false);
  };

  if (!value || replacing) {
    return (
      <div className="space-y-2" data-testid="portfolio-video-cover-empty">
        <PortfolioVideoUploadField value="" onChange={handleUploaded} testId="portfolio-video-cover-upload" />
        {replacing && (
          <button
            type="button"
            data-testid="portfolio-video-cover-cancel-replace"
            onClick={() => setReplacing(false)}
            className="text-xs text-[#A3A3A3] hover:text-white"
          >
            Cancel — keep current video
          </button>
        )}
      </div>
    );
  }

  const w = meta?.width || 0;
  const h = meta?.height || 0;
  const details = [w && h ? `${w}×${h}` : "", shapeLabel(w, h), meta?.duration ? `${meta.duration}s` : ""].filter(Boolean).join(" · ");

  return (
    <div className="space-y-3" data-testid="portfolio-video-cover-preview">
      <video
        key={value}
        src={value}
        controls
        muted
        playsInline
        preload="metadata"
        data-testid="portfolio-video-cover-player"
        className="w-full max-h-72 rounded-xl bg-black object-contain border border-[#262626]"
      />
      <div className="flex items-center justify-between gap-3">
        <div className="text-[11px] text-[#A3A3A3] truncate" data-testid="portfolio-video-cover-details">
          {details || "Video uploaded"}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            data-testid="portfolio-video-cover-replace"
            onClick={() => setReplacing(true)}
            className="inline-flex items-center gap-1.5 rounded-full border border-[#333] px-3 py-1.5 text-xs text-white hover:border-[#FF6600]"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Replace
          </button>
          <button
            type="button"
            data-testid="portfolio-video-cover-remove"
            onClick={() => onChange("", null)}
            className="inline-flex items-center gap-1.5 rounded-full border border-[#333] px-3 py-1.5 text-xs text-[#F87171] hover:border-[#F87171]"
          >
            <Trash2 className="w-3.5 h-3.5" /> Remove
          </button>
        </div>
      </div>
    </div>
  );
};
